import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { DashboardSwitcher } from './DashboardSwitcher';
import { ReferralCenter } from './referrals/ReferralCenter';
import { MessageDashboard } from './messaging/MessageDashboard';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { createSafeHtml } from '../lib/sanitizeHtml';
import { missingProfileDetailFields, profileDetailsAreComplete, selfServiceProfileEditOpen, splitStoredName, PROFILE_DETAILS_ADMIN_NOTE, PROFILE_DETAILS_MISSING_NOTE } from '../lib/profileDetailsEdit.js';
import BirthdayPackageButtons from './BirthdayPackageButtons';

import { ComprehensiveAffiliateManager } from './affiliate/ComprehensiveAffiliateManager';
import { AffiliateTrainingCenter } from './affiliate/AffiliateTrainingCenter';
import { MarketingToolsManager } from './affiliate/MarketingToolsManager';
import { SalesAnalytics } from './affiliate/SalesAnalytics';
import { ProfilePictureUpload } from './ProfilePictureUpload';
import { getFullVersion } from '../lib/version';
import { DIVIDEND_PROJECTIONS, CALCULATION_CONSTANTS, computeDynamicAnnualProjection } from '../lib/services/calculationService';
import { useLiveTotalSharesSold } from '../lib/hooks/useLiveTotalSharesSold';
import { DIVIDENDS_USER_VISIBLE } from '../lib/dividendsUserVisible';
import { MasterAdminViewWrapper } from './admin/MasterAdminViewBanner';
import { PhaseCountdownTimer } from './PhaseCountdownTimer';
import { SecurePasswordChangeForm } from './user/SecurePasswordChangeForm';
import { PinVerificationModal } from './PinVerificationModal';
import { useCompleteCountryList } from '../lib/hooks/useCompleteCountryList';
import { CreateUserModal } from './affiliate/CreateUserModal';
import { getPhoneCodeForCountry, formatPhoneWithCountryCode } from '../lib/data/countryPhoneCodes';
import { extractNumericId } from '../lib/userIdExtractor';
import { ensureSessionHeaders, getSessionHeaders } from '../lib/serverSession';
import GoldDiggersClub from './competition/GoldDiggersClub';
import { GoldDiggersDashboard } from './GoldDiggersDashboard';
import { ZimbabweAwaitsPage } from './ZimbabweAwaitsPage';
import { ProductionVeinAccord } from './ProductionVeinAccord';
import { isGlobalTopAffiliatePoolTester } from '../lib/productionVeinAccord';
import { FoundingShareholdersShowcase } from './founding-shareholders/FoundingShareholdersShowcase';
import { HowToStartVideos } from './HowToStartVideos';
import ReferralLinkRequirements from './referrals/ReferralLinkRequirements';
import { UpdatesPage } from './user/UpdatesPage';
// ShareBreakdown type for API response
interface ShareBreakdown {
  username: string;
  directPurchases: number;
  bonusShares: number;
  commissionLevelShares: number;
  presaleCommissionShares: number;
  convertedShares: number;
  multiLevelShares: number;
  netTransfers: number;
  transfersSent: number;
  transfersReceived: number;
  legacyShares: number;
  totalShares: number;
  wholeShares: number;
  fractionalShares: number;
}
import { LiveActivityFeed } from './calculator/LiveActivityFeed';

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface AffiliateDashboardProps {
  user: any;
  onLogout: () => void;
  onSwitchDashboard: (dashboard: 'shareholder' | 'affiliate') => void;
}

// TipTap WYSIWYG Editor Component for Profile Description
const ProfileDescriptionEditor: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        'data-placeholder': 'Click here to type your profile description...'
      }
    }
  })

  // Update editor content when value prop changes
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '')
    }
  }, [value, editor])

  if (!editor) {
    return null
  }

  return (
    <div style={{ backgroundColor: '#374151', borderRadius: '0.5rem', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '8px',
        backgroundColor: '#1F2937',
        borderBottom: '1px solid #4B5563',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('bold') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('bold') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          B
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('italic') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('italic') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontStyle: 'italic'
          }}
        >
          I
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('strike') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('strike') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            textDecoration: 'line-through'
          }}
        >
          S
        </button>
        <div style={{ width: '1px', backgroundColor: '#4B5563', margin: '0 4px' }} />
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('heading', { level: 1 }) ? '#FBBF24' : '#4B5563',
            color: editor.isActive('heading', { level: 1 }) ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          H1
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('heading', { level: 2 }) ? '#FBBF24' : '#4B5563',
            color: editor.isActive('heading', { level: 2 }) ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          H2
        </button>
        <div style={{ width: '1px', backgroundColor: '#4B5563', margin: '0 4px' }} />
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('bulletList') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('bulletList') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          • List
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('orderedList') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('orderedList') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          1. List
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('blockquote') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('blockquote') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          " Quote
        </button>
        <button
          onClick={() => editor.chain().focus().clearNodes().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: '#4B5563',
            color: '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Clear
        </button>
      </div>
      {/* Editor */}
      <div
        onClick={() => editor.chain().focus().run()}
        style={{
          minHeight: '200px',
          backgroundColor: '#4B5563',
          padding: '12px',
          cursor: 'text'
        }}
      >
        <style>{`
          .ProseMirror {
            min-height: 176px;
            outline: none;
            color: #FFFFFF;
            font-size: 14px;
            line-height: 1.6;
          }
          .ProseMirror p {
            margin: 0 0 0.5em 0;
          }
          .ProseMirror:focus {
            outline: none;
          }
          .ProseMirror p.is-editor-empty:first-child::before {
            color: #9CA3AF;
            content: attr(data-placeholder);
            float: left;
            height: 0;
            pointer-events: none;
          }
        `}</style>
        <EditorContent
          editor={editor}
        />
      </div>
    </div>
  )
}

type AffiliateSection =
  | 'overview'
  | 'referrals'
  | 'commissions'
  | 'sales-analytics'
  | 'marketing-tools'
  | 'messages'
  | 'training'
  | 'gold-diggers'
  | 'gold-diggers-pool'
  | 'vein-accord'
  | 'zimbabwe-awaits'
  | 'founding-shareholders'
  | 'settings'
  | 'updates';

const affiliateNavigation = [
  {
    id: 'overview' as AffiliateSection,
    label: 'Overview',
    icon: '📊',
    description: 'Commission summary and performance'
  },
  {
    id: 'referrals' as AffiliateSection,
    label: 'Referrals',
    icon: '🤝',
    description: 'Manage referral links and track conversions'
  },
  {
    id: 'commissions' as AffiliateSection,
    label: 'Commissions',
    icon: '💰',
    description: 'Track USDT and share commissions'
  },
  {
    id: 'sales-analytics' as AffiliateSection,
    label: 'Sales Analytics',
    icon: '📈',
    description: 'Track daily, weekly, and monthly share sales'
  },
  {
    id: 'marketing-tools' as AffiliateSection,
    label: 'Marketing Tools',
    icon: '🎯',
    description: 'Banners, links, and promotional materials'
  },
  {
    id: 'gold-diggers' as AffiliateSection,
    label: 'Gold Diggers',
    icon: '🏆',
    description: 'Competition leaderboard and prizes'
  },
  {
    id: 'gold-diggers-pool' as AffiliateSection,
    label: 'Performance Pool',
    icon: '⛏️',
    description: 'Daily 10% pool — Top 50 teams win',
    isNew: true
  },
  {
    id: 'vein-accord' as AffiliateSection,
    label: 'Global Top Affiliate Pool',
    icon: '🪙',
    description: 'Production Capital paid-share pool on your Daily Performance Pool legs',
    isNew: true
  },
  {
    id: 'zimbabwe-awaits' as AffiliateSection,
    label: 'Zimbabwe Awaits',
    icon: '🇿🇼',
    description: 'Top 10 team leaders: mine visit + 10% share bonus',
    isNew: true
  },
  {
    id: 'updates' as AffiliateSection,
    label: 'Updates',
    icon: '📢',
    description: 'Published updates for affiliates'
  },
  {
    id: 'messages' as AffiliateSection,
    label: 'Messages',
    icon: '💬',
    description: 'Internal messaging system'
  },
  {
    id: 'training' as AffiliateSection,
    label: 'Training',
    icon: '🎓',
    description: 'Learn effective referral strategies'
  },
  {
    id: 'founding-shareholders' as AffiliateSection,
    label: 'Founding Shareholders',
    icon: '🏆',
    description: 'Recognized founding shareholders'
  },
  {
    id: 'settings' as AffiliateSection,
    label: 'Settings',
    icon: '⚙️',
    description: 'Account preferences'
  }
];

export const AffiliateDashboard: React.FC<AffiliateDashboardProps> = ({
  user: propUser,
  onLogout,
  onSwitchDashboard
}) => {
  // Local user state to allow updates
  const [user, setUser] = useState<any>(propUser || null);
  const [activeSection, setActiveSection] = useState<AffiliateSection>(() => {
    // Check URL parameters for initial section
    const urlParams = new URLSearchParams(window.location.search);
    const sectionParam = urlParams.get('section');
    const validSections: AffiliateSection[] = ['overview', 'referrals', 'commissions', 'sales-analytics', 'marketing-tools', 'messages', 'training', 'gold-diggers', 'gold-diggers-pool', 'vein-accord', 'zimbabwe-awaits', 'founding-shareholders', 'settings'];
    if (sectionParam && validSections.includes(sectionParam as AffiliateSection)) {
      return sectionParam as AffiliateSection;
    }
    return 'overview';
  });
  const [commissionData, setCommissionData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // CRITICAL: Store validated numeric user ID for child components
  const [validatedUserId, setValidatedUserId] = useState<number | null>(null);

  // Live total shares sold (dynamic denominator for Jul/Oct 2026 + Jan 2027).
  // While shares are still selling, the projected 2026 dividend per share
  // scales up materially vs the 1.4M-cap floor.
  const { totalSharesSold: liveSharesSold } = useLiveTotalSharesSold();

  /**
   * CRITICAL HELPER: Get valid user ID using multiple fallback strategies
   * This matches the pattern used in UserDashboard for consistency
   */
  const getValidatedUserId = (): number | null => {
    // Strategy 1: database_user.id
    if (user?.database_user?.id) {
      const id = extractNumericId(user.database_user.id);
      if (id) return id;
    }
    // Strategy 2: user_metadata.user_id
    if (user?.user_metadata?.user_id) {
      const id = extractNumericId(user.user_metadata.user_id);
      if (id) return id;
    }
    // Strategy 3: Direct user.id
    if (user?.id) {
      const id = extractNumericId(user.id);
      if (id) return id;
    }
    return null;
  };

  // CRITICAL FIX: Check if user.database_user.id is a UUID instead of integer
  useEffect(() => {
    if (user?.database_user?.id) {
      const dbUserId = user.database_user.id;

      // Check if database_user.id is a UUID (string with dashes) instead of integer
      if (typeof dbUserId === 'string' && dbUserId.includes('-')) {
        console.error('⚠️ CRITICAL: database_user.id is UUID, not integer!');
        console.error('⚠️ UUID found:', dbUserId);
        console.error('⚠️ User needs to log out and log back in to fix this issue.');
      }
    }
  }, [user]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);

  // Scroll to top whenever section changes (especially important for mobile)
  useEffect(() => {
    // Use setTimeout to ensure content is rendered before scrolling
    setTimeout(() => {
      window.scrollTo(0, 0); // Instant scroll, no smooth behavior
    }, 100);
  }, [activeSection]);

  // Add/remove class to body when mobile menu opens/closes to hide news ticker
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('affiliate-mobile-menu-open');
    } else {
      document.body.classList.remove('affiliate-mobile-menu-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('affiliate-mobile-menu-open');
    };
  }, [isMobileMenuOpen]);
  const [referralLink, setReferralLink] = useState<string>('');

  // Sync local user state with prop changes
  useEffect(() => {
    if (propUser) {
      setUser(propUser);
    }
  }, [propUser]);

  useEffect(() => {
    const userId = extractNumericId(user?.database_user?.id || user?.id);
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ userId, dataTypes: ['profile'] })
        });
        const result = await response.json();
        const profile = result?.data?.profile;
        if (cancelled || !response.ok || !profile) return;
        setUser((prev: any) => ({
          ...prev,
          database_user: {
            ...prev?.database_user,
            ...profile,
            profile_details_edit_used: profile.profile_details_edit_used !== false
          }
        }));
        if (!profileDetailsAreComplete(profile)) {
          setMustCompleteProfile(true);
          setActiveSection('settings');
        } else {
          setMustCompleteProfile(false);
        }
      } catch {
        /* leave the member on the current page if the check cannot be read */
      }
    })();
    return () => { cancelled = true; };
  }, [user?.database_user?.id, user?.id]);

  useEffect(() => {
    if (activeSection !== 'settings') return;
    const userId = extractNumericId(user?.database_user?.id || user?.id);
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ userId, dataTypes: ['profile'] })
        });
        const result = await response.json();
        const flag = result?.data?.profile?.profile_details_edit_used;
        if (!response.ok || cancelled || flag === undefined) return;
        setUser((prev: any) => ({
          ...prev,
          database_user: {
            ...prev?.database_user,
            profile_details_edit_used: flag !== false
          }
        }));
      } catch {
        // Missing flag stays locked. The edit button stays hidden.
      }
    })();
    return () => { cancelled = true; };
  }, [activeSection]);

  // Settings states
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [mustCompleteProfile, setMustCompleteProfile] = useState(false);
  const [profileEditorData, setProfileEditorData] = useState({
    full_name: '',
    email: '',
    phone: '',
    country_of_residence: '',
    profile_description: ''
  });
  const [profileValidation, setProfileValidation] = useState({
    full_name: { status: 'idle', message: '' },
    email: { status: 'idle', message: '' },
    phone: { status: 'idle', message: '' },
    country_of_residence: { status: 'idle', message: '' }
  });

  // 2FA states
  const [pending2FAState, setPending2FAState] = useState<boolean | null>(null);
  const [show2FAPinModal, setShow2FAPinModal] = useState(false);

  // Notification preferences states
  const [notificationPreferences, setNotificationPreferences] = useState({
    payment_confirmations: true,
    security_alerts: true,
    commission_updates: true,
    marketing_emails: true,
    in_app_notifications: true,
    sound_notifications: false,
    notification_frequency: 'immediate'
  });
  const [notificationPreferencesLoading, setNotificationPreferencesLoading] = useState(false);
  const [notificationPreferencesMessage, setNotificationPreferencesMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Get complete country list with getCountryName function
  const { getCountryName, countryOptions } = useCompleteCountryList({
    prioritizeCountries: ['ZAF', 'NAM', 'SWZ'] // South Africa, Namibia, Eswatini at top
  });

  useEffect(() => {
    loadCommissionData();

    // Auto-refresh commission data every 30 seconds
    const refreshInterval = setInterval(() => {
      loadCommissionData();
    }, 30000); // 30 seconds

    return () => clearInterval(refreshInterval);
  }, [user]);

  // Load referral link when user ID is validated
  useEffect(() => {
    if (validatedUserId || user?.database_user?.username || user?.username) {
      loadReferralLink();
    }
  }, [validatedUserId, user?.database_user?.username, user?.username]);

  useEffect(() => {
    if (user?.database_user?.id) {
      loadNotificationPreferences();
    }
  }, [user?.database_user?.id]);

  const loadReferralLink = async () => {
    let username = user?.database_user?.username || user?.username;

    // If no username found in user object, try to fetch from database via API
    if (!username && validatedUserId) {
      console.log('🔍 [AffiliateDashboard] No username in user object, fetching from database...');
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/affiliate-data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ userId: validatedUserId, dataTypes: ['username'] })
        })

        const result = await response.json()
        if (response.ok && result.data?.username) {
          username = result.data.username;
          console.log('✅ [AffiliateDashboard] Fetched username from database:', username);
        }
      } catch (error) {
        console.error('❌ [AffiliateDashboard] Failed to fetch username:', error);
      }
    }

    if (username) {
      setReferralLink(`${window.location.origin}/${username}`);
      console.log('🔗 [AffiliateDashboard] Referral link set:', `${window.location.origin}/${username}`);
    } else {
      console.warn('⚠️ [AffiliateDashboard] No username available for referral link');
    }
  };



  const handleProfilePictureUpdate = async (newImageUrl: string) => {
    console.log('🖼️ Updating profile picture across all components:', newImageUrl);

    // Small delay to ensure database update has completed
    await new Promise(resolve => setTimeout(resolve, 100));

    // Update the user object immediately
    if (user?.database_user) {
      user.database_user.profile_image_url = newImageUrl;
      user.database_user.updated_at = new Date().toISOString();
    }

    // Update localStorage for persistence with timestamp
    const currentTimestamp = new Date().toISOString();
    const storedUser = localStorage.getItem('aureus_user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      userData.profile_image_url = newImageUrl;
      userData.updated_at = currentTimestamp;
      localStorage.setItem('aureus_user', JSON.stringify(userData));
      console.log('✅ Profile picture URL updated in localStorage with timestamp');
    }

    // Force a re-render by updating state with the new URL
    const updatedUser = { ...user };
    if (updatedUser.database_user) {
      updatedUser.database_user.profile_image_url = newImageUrl;
      updatedUser.database_user.updated_at = currentTimestamp;
    }
    setUser(updatedUser);

    // Show success message
    setSettingsMessage({ type: 'success', text: 'Profile picture updated successfully!' });
    setTimeout(() => setSettingsMessage(null), 3000);

    console.log('✅ Profile picture update completed with timestamp:', currentTimestamp);
  };

  const loadCommissionData = async () => {
    // CRITICAL: Extract numeric ID using multiple fallback strategies
    console.log('🔄 [AffiliateDashboard] loadCommissionData called');
    console.log('🔍 [AffiliateDashboard] User object:', {
      hasUser: !!user,
      hasDatabaseUser: !!user?.database_user,
      databaseUserId: user?.database_user?.id,
      databaseUserIdType: typeof user?.database_user?.id,
      userId: user?.id,
      userIdType: typeof user?.id,
      userMetadataUserId: user?.user_metadata?.user_id
    });

    // Use centralized helper for ID extraction
    const numericUserId = getValidatedUserId();
    console.log('🔢 [AffiliateDashboard] Extracted numeric user ID:', numericUserId);

    if (!numericUserId) {
      console.error('❌ [AffiliateDashboard] Failed to extract numeric user ID from any source');
      console.error('❌ [AffiliateDashboard] Full user object:', JSON.stringify(user, null, 2));
      setLoading(false);
      setValidatedUserId(null);
      return;
    }

    // CRITICAL: Store validated ID for child components
    setValidatedUserId(numericUserId);

    try {
      // Use API endpoint to bypass RLS (service role key is server-side)
      console.log('🔧 [AffiliateDashboard] Loading data via API endpoint');

      const response = await fetch(`${API_BASE_URL}/api/user/affiliate-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId: numericUserId, dataTypes: ['all'] })
      })

      const apiResult = await response.json()
      if (!response.ok) {
        console.error('❌ [AffiliateDashboard] API error:', apiResult.error)
        throw new Error(apiResult.error || 'Failed to load affiliate data')
      }

      const { data: apiData } = apiResult

      // Extract data from API response
      const commissions = apiData.commission_balance
      const referrals = { length: apiData.referral_count }
      const __sharePurchases = apiData.share_purchases
      const commissionSharePurchases = apiData.commission_share_purchases
      const conversions = apiData.conversions
      const __sentTransfers = apiData.sent_transfers
      const __receivedTransfers = apiData.received_transfers

      console.log('📊 [AffiliateDashboard] Commission balances result:', {
        hasData: !!commissions,
        usdtBalance: commissions?.usdt_balance,
        shareBalance: commissions?.share_balance
      });

      console.log('👥 [AffiliateDashboard] Referrals result:', {
        count: apiData.referral_count
      });

      // ========================================
      // UNIFIED SHARE CALCULATION API
      // ========================================
      // Use the single source of truth for share calculations
      // This ensures consistency between UserDashboard and AffiliateDashboard
      const shareResponse = await fetch(`${API_BASE_URL}/api/user/share-calculations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId: numericUserId, operation: 'calculate_user_shares' })
      });
      const shareResult = await shareResponse.json();
      const shareBreakdown: ShareBreakdown = shareResult.data || {
        username: '',
        directPurchases: 0,
        bonusShares: 0,
        commissionLevelShares: 0,
        presaleCommissionShares: 0,
        convertedShares: 0,
        multiLevelShares: 0,
        netTransfers: 0,
        legacyShares: 0,
        totalShares: 0,
        wholeShares: 0
      };

      // Calculate USDT converted (still needed for display)
      const totalUsdtConverted = conversions?.reduce((sum: number, conversion: any) => sum + parseFloat(conversion.usdt_amount), 0) || 0;

      console.log('📊 AFFILIATE DASHBOARD - UNIFIED SHARE CALCULATION:', {
        userId: user.database_user.id,
        username: shareBreakdown.username,
        directPurchases: shareBreakdown.directPurchases,
        bonusShares: shareBreakdown.bonusShares,
        commissionLevelShares: shareBreakdown.commissionLevelShares,
        presaleCommissionShares: shareBreakdown.presaleCommissionShares,
        convertedShares: shareBreakdown.convertedShares,
        netTransfers: shareBreakdown.netTransfers,
        legacyShares: shareBreakdown.legacyShares,
        TOTAL: shareBreakdown.totalShares,
        wholeShares: shareBreakdown.wholeShares
      });

      // Calculate 2026 Projected Dividend using central DIVIDEND_PROJECTIONS
      // 2026-2027 are designated growth years; figure reflects first eligible quarterly payout
      const proj2026 = DIVIDEND_PROJECTIONS[2026];
      const annualGoldKg = proj2026?.goldProductionKgPerYear || 1200;
      const firstQuarterlyPayout = proj2026?.quarterlyPayouts?.[0]?.amount || 0;
      const projectedDividend = Math.round(firstQuarterlyPayout * shareBreakdown.totalShares * 100) / 100;

      setCommissionData({
        ...commissions,
        computed_withdrawable_balance: commissions?.computed_withdrawable_balance ?? commissions?.usdt_balance ?? 0,
        referral_count: referrals?.length || 0,
        // ========================================
        // ALL SHARE BREAKDOWN FIELDS FROM UNIFIED SERVICE
        // ========================================
        // Direct purchases from aureus_share_purchases (non-COMMISSION_LEVEL)
        direct_purchases: shareBreakdown.directPurchases,
        // Tier bonus shares from aureus_share_purchases.bonus_shares
        bonus_shares: shareBreakdown.bonusShares,
        // Commission level shares (COMMISSION_LEVEL_X payments)
        commission_level_shares: shareBreakdown.commissionLevelShares,
        // Presale 15% commission shares
        presale_commission_shares: shareBreakdown.presaleCommissionShares,
        // USDT converted to shares
        converted_shares: shareBreakdown.convertedShares,
        total_usdt_converted: totalUsdtConverted,
        // Transfers
        shares_sent: shareBreakdown.transfersSent,
        shares_received: shareBreakdown.transfersReceived,
        net_transferred_shares: shareBreakdown.netTransfers,
        // Legacy shares (historical adjustments)
        legacy_shares: shareBreakdown.legacyShares,
        // Totals
        total_shares_owned: shareBreakdown.totalShares,
        whole_shares: shareBreakdown.wholeShares,
        fractional_shares: shareBreakdown.fractionalShares,
        // For backward compatibility - combined purchased shares (direct + bonus)
        purchased_shares: shareBreakdown.directPurchases + shareBreakdown.bonusShares,
        // Earned shares = presale + commission level shares
        commission_shares_only: shareBreakdown.presaleCommissionShares + shareBreakdown.commissionLevelShares,
        // Add projected dividend
        projected_dividend: projectedDividend,
        annual_gold_kg: annualGoldKg,
        // 5% Commission Share Purchases - individual purchase records for display
        commission_share_purchases: commissionSharePurchases || []
      });
    } catch (error) {
      console.error('Error loading commission data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Settings handler functions — opens magic link verification modal before toggling 2FA
  const handle2FAToggle = (enabled: boolean) => {
    const userId = extractNumericId(user?.database_user?.id);
    if (!userId) return;
    setPending2FAState(enabled);
    setShow2FAPinModal(true);
  };

  // Called after the user enters the 6-digit email PIN and the modal confirms verification
  const handle2FAVerified = async (verifiedToken?: string) => {
    const userId = extractNumericId(user?.database_user?.id);
    if (!userId || pending2FAState === null) return;

    // v4.6.3: PinVerificationModal no longer calls onClose on success — close it ourselves.
    setShow2FAPinModal(false);

    setSettingsLoading(true);
    setSettingsMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/toggle-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId, enabled: pending2FAState, token: verifiedToken })
      });

      const result = await response.json();

      if (result.success) {
        setSettingsMessage({
          type: 'success',
          text: `2FA ${pending2FAState ? 'enabled' : 'disabled'} successfully`
        });
        setTimeout(() => setSettingsMessage(null), 3000);

        // Reload user data
        window.location.reload();
      } else {
        setSettingsMessage({
          type: 'error',
          text: result.error || 'Failed to update 2FA settings'
        });
      }
    } catch (error) {
      console.error('Error toggling 2FA:', error);
      setSettingsMessage({
        type: 'error',
        text: 'Failed to update 2FA settings'
      });
    } finally {
      setSettingsLoading(false);
      setPending2FAState(null);
    }
  };

  const handle2FAPinModalClose = () => {
    setShow2FAPinModal(false);
    setPending2FAState(null);
    setSettingsLoading(false);
  };

  const openProfileEditor = async () => {
    if (!selfServiceProfileEditOpen(user?.database_user)) {
      setSettingsMessage({ type: 'error', text: PROFILE_DETAILS_ADMIN_NOTE });
      return;
    }
    try {
      // CRITICAL: Fetch fresh data from database instead of using stale user state
      const userId = extractNumericId(user?.database_user?.id);

      if (!userId) {
        console.error('❌ Cannot open profile editor: Invalid user ID');
        return;
      }

      console.log('🔄 Fetching fresh profile data from database for user:', userId);

      // Use regular supabase client (respects RLS)
      const { data: freshUserData, error } = await supabase
        .from('users')
        .select('full_name, email, phone, country_of_residence, profile_description')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('❌ Error fetching fresh profile data:', error);
        // Fallback to current user state if fetch fails
        const countryCode = user?.database_user?.country_of_residence || '';
        setProfileEditorData({
          full_name: user?.database_user?.full_name || '',
          email: user?.database_user?.email || user?.email || '',
          phone: user?.database_user?.phone || '',
          country_of_residence: countryCode,
          profile_description: user?.database_user?.profile_description || ''
        });
      } else if (freshUserData) {
        console.log('✅ Fresh profile data loaded:', {
          full_name: freshUserData.full_name,
          email: freshUserData.email,
          phone: freshUserData.phone,
          country_of_residence: freshUserData.country_of_residence
        });

        setProfileEditorData({
          full_name: freshUserData.full_name || '',
          email: freshUserData.email || '',
          phone: freshUserData.phone || '',
          country_of_residence: freshUserData.country_of_residence || '',
          profile_description: freshUserData.profile_description || ''
        });
      }

      setProfileValidation({
        full_name: { status: 'idle', message: '' },
        email: { status: 'idle', message: '' },
        phone: { status: 'idle', message: '' },
        country_of_residence: { status: 'idle', message: '' }
      });
      setShowProfileEditor(true);
    } catch (error) {
      console.error('❌ Error opening profile editor:', error);
    }
  };

  // Validation functions with debouncing
  const validateFullName = useCallback((name: string) => {
    if (!name.trim()) {
      return { status: 'invalid', message: '❌ Name is required' };
    }

    // Check for invalid characters (no periods, only letters, spaces, hyphens, apostrophes)
    if (!/^[a-zA-Z\s\-']+$/.test(name.trim())) {
      return { status: 'invalid', message: '❌ Name may only contain letters, spaces, hyphens, and apostrophes. No periods or special characters.' };
    }

    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length < 2) {
      return { status: 'invalid', message: '⚠️ Please enter both first and last name' };
    }

    // Check each part is at least 2 characters (no single letters)
    for (const part of nameParts) {
      if (part.length < 2) {
        return { status: 'invalid', message: '❌ Each part of your name must be at least 2 characters. No single letters.' };
      }
    }

    return { status: 'valid', message: '✅ Name looks good' };
  }, []);

  const validateEmail = useCallback((email: string) => {
    if (!email.trim()) {
      return { status: 'invalid', message: '❌ Email is required' };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { status: 'invalid', message: '❌ Invalid email format' };
    }
    return { status: 'valid', message: '✅ Email looks good' };
  }, []);

  const validatePhone = useCallback((phone: string, country: string) => {
    if (!phone.trim()) {
      return { status: 'idle', message: '' };
    }
    if (!country) {
      return { status: 'invalid', message: '⚠️ Please select a country first' };
    }
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 7) {
      return { status: 'invalid', message: '⚠️ Phone number too short' };
    }
    if (digitsOnly.length > 15) {
      return { status: 'invalid', message: '⚠️ Phone number too long' };
    }
    return { status: 'valid', message: '✅ Phone number valid' };
  }, []);

  const validateCountry = useCallback((country: string) => {
    if (!country) {
      return { status: 'invalid', message: '⚠️ Please select a country' };
    }
    return { status: 'valid', message: '✅ Country selected' };
  }, []);

  // Debounced validation handlers
  useEffect(() => {
    const timer = setTimeout(() => {
      if (profileEditorData.full_name) {
        setProfileValidation(prev => ({
          ...prev,
          full_name: validateFullName(profileEditorData.full_name)
        }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [profileEditorData.full_name, validateFullName]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (profileEditorData.email) {
        setProfileValidation(prev => ({
          ...prev,
          email: validateEmail(profileEditorData.email)
        }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [profileEditorData.email, validateEmail]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (profileEditorData.phone) {
        setProfileValidation(prev => ({
          ...prev,
          phone: validatePhone(profileEditorData.phone, profileEditorData.country_of_residence)
        }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [profileEditorData.phone, profileEditorData.country_of_residence, validatePhone]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (profileEditorData.country_of_residence) {
        setProfileValidation(prev => ({
          ...prev,
          country_of_residence: validateCountry(profileEditorData.country_of_residence)
        }));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [profileEditorData.country_of_residence, validateCountry]);

  const handleProfileUpdate = async () => {
    // Use centralized user ID extraction
    const rawUserId = user?.database_user?.id ||
                     user?.user_metadata?.user_id;

    const userId = extractNumericId(rawUserId);

    // Validate we have a valid user ID
    if (!userId) {
      console.error('❌ Cannot update profile: Invalid user ID', {
        raw_id: rawUserId,
        parsed_id: userId
      });
      setSettingsMessage({
        type: 'error',
        text: 'Unable to update profile: Invalid user ID'
      });
      return;
    }

    // Validate full name has both first and last name
    const nameParts = profileEditorData.full_name.trim().split(/\s+/);
    if (!profileEditorData.full_name.trim() || nameParts.length < 2) {
      setSettingsMessage({
        type: 'error',
        text: '❌ Please enter both your first and last name'
      });
      return;
    }

    setSettingsLoading(true);
    setSettingsMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'update_profile',
          data: {
            full_name: profileEditorData.full_name,
            phone: profileEditorData.phone,
            country_of_residence: profileEditorData.country_of_residence,
            profile_description: profileEditorData.profile_description
          }
        })
      });
      let updateResult: any = null;
      try {
        updateResult = await response.json();
      } catch {
        throw new Error('The profile service did not return a result. Refresh the page and try again.');
      }
      if (!response.ok || !updateResult?.data?.user) {
        const serverMessage = updateResult?.message || updateResult?.error || 'Failed to update profile';
        throw new Error(typeof serverMessage === 'string' ? serverMessage : 'Failed to update profile');
      }
      const savedUser = updateResult.data.user;

      setUser((prev: any) => {
        if (!prev) return prev;
        return {
          ...prev,
          database_user: {
            ...prev?.database_user,
            ...savedUser,
            profile_details_edit_used: true
          }
        };
      });
      if (profileDetailsAreComplete({
        full_name: profileEditorData.full_name,
        phone: profileEditorData.phone,
        country_of_residence: profileEditorData.country_of_residence
      })) {
        setMustCompleteProfile(false);
      }

      setSettingsMessage({
        type: 'success',
        text: '✅ Profile updated successfully!'
      });

      setShowProfileEditor(false);
      setTimeout(() => setSettingsMessage(null), 3000);

    } catch (error: unknown) {
      console.error('❌ Error updating profile:', error);
      const rawMessage = error instanceof Error
        ? error.message
        : (error && typeof error === 'object' && typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : '');
      setSettingsMessage({
        type: 'error',
        text: `Failed to update profile: ${rawMessage || 'The profile was not saved. Refresh the page and try again.'}`
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleNotificationPreferenceChange = (key: string, value: any) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveNotificationPreferences = async () => {
    // CRITICAL: Must use parsed user ID to prevent "invalid input syntax for type integer" errors
    const userId = extractNumericId(user?.database_user?.id);
    if (!userId) return;

    setNotificationPreferencesLoading(true);
    setNotificationPreferencesMessage(null);

    try {
      // Use regular supabase client (respects RLS)
      const preferencesData = {
        user_id: userId,
        payment_confirmations: notificationPreferences.payment_confirmations,
        security_alerts: notificationPreferences.security_alerts,
        commission_updates: notificationPreferences.commission_updates,
        marketing_emails: notificationPreferences.marketing_emails,
        in_app_notifications: notificationPreferences.in_app_notifications,
        sound_notifications: notificationPreferences.sound_notifications,
        notification_frequency: notificationPreferences.notification_frequency,
        audio_enabled: notificationPreferences.sound_notifications,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('user_notification_preferences')
        .upsert(preferencesData, { onConflict: 'user_id' });

      if (error) {
        throw error;
      }

      setNotificationPreferencesMessage({
        type: 'success',
        text: 'Notification preferences saved successfully!'
      });
      setTimeout(() => setNotificationPreferencesMessage(null), 3000);

    } catch (error) {
      console.error('Error saving notification preferences:', error);
      setNotificationPreferencesMessage({
        type: 'error',
        text: 'Failed to save notification preferences'
      });
    } finally {
      setNotificationPreferencesLoading(false);
    }
  };

  const loadNotificationPreferences = async () => {
    if (!user?.database_user?.id) return;

    try {
      const numericUserId = extractNumericId(user.database_user.id);

      // Call server-side API endpoint instead of using service role client on frontend.
      // v4.8.68: send session headers — the endpoint is withSessionAuth-protected and
      // derives the user from req.user.id (query param is ignored server-side). Without
      // X-Session-Id it returned 401 on every affiliate-dashboard load.
      const response = await fetch(`/api/get-notification-preferences?userId=${numericUserId}`, {
        headers: { ...getSessionHeaders() }
      });

      if (!response.ok) {
        console.error('Failed to fetch notification preferences:', response.statusText);
        return;
      }

      const data = await response.json();

      if (data && data.user_id) {
        setNotificationPreferences({
          payment_confirmations: data.payment_confirmations ?? true,
          security_alerts: data.security_alerts ?? true,
          commission_updates: data.commission_updates ?? true,
          marketing_emails: data.marketing_emails ?? true,
          in_app_notifications: data.in_app_notifications ?? true,
          sound_notifications: data.sound_notifications ?? false,
          notification_frequency: data.notification_frequency || 'immediate'
        });
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    }
  };

  const clientDisplayBalance = Math.max(0, Number(commissionData?.displayed_balance ?? commissionData?.usdt_balance ?? 0) || 0);
  const clientWithdrawableBalance = Math.max(0, Number(commissionData?.computed_withdrawable_balance ?? clientDisplayBalance) || 0);

  const renderOverview = () => (
    <div className="space-y-6 w-full max-w-full overflow-x-hidden box-border">
      {/* Phase Countdown Timer */}
      <PhaseCountdownTimer variant="dashboard" />

      {/* Welcome Header - Mobile Optimized */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-4 sm:p-6 text-white shadow-lg w-full max-w-full overflow-x-hidden box-border">
        <h2 className="text-xl sm:text-2xl font-bold mb-2">
          Welcome to Your Affiliate Dashboard! 🤝
        </h2>
        <p className="text-blue-100 text-sm sm:text-base break-words">
          Refer friends, earn commissions, and grow your income with Aureus Alliance Holdings.
        </p>
      </div>

      {/* Key Metrics Cards - Mobile First Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-6 w-full max-w-full overflow-x-hidden box-border">
        {/* Available Balance - Mobile Optimized */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-3 sm:p-4 lg:p-6 shadow-lg">
          <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <p className="text-white text-sm sm:text-sm font-medium">Available Balance</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-400">
                ${clientWithdrawableBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-green-600 p-2 sm:p-3 rounded-lg self-start sm:self-auto">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
          </div>
          <div className="mt-2 sm:mt-3 lg:mt-4">
            <p className="text-white text-sm sm:text-sm">
              Commission Balance: ${clientDisplayBalance.toFixed(2)}
            </p>
          </div>
        </div>

        {DIVIDENDS_USER_VISIBLE && (() => {
          // Overlay the live-denominator dynamic projection so the displayed
          // figure reflects actual shares sold (~141k) not the 1.4M cap floor.
          const staticProj = DIVIDEND_PROJECTIONS[2026];
          const dyn = computeDynamicAnnualProjection(2026, liveSharesSold);
          const annualPerShare = dyn?.estimatedDividendPerShare ?? 0;
          const userShares = commissionData?.total_shares_owned || 0;
          const liveProjected = annualPerShare * userShares;
          const denominator = dyn ? Math.floor(dyn.denominatorUsed) : 0;
          return (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-3 sm:p-4 lg:p-6 shadow-lg">
          <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <p className="text-white text-sm sm:text-sm font-medium">2026 Projected Dividend</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-yellow-400">
                ${liveProjected.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
            </div>
            <div className="bg-yellow-600 p-2 sm:p-3 rounded-lg self-start sm:self-auto">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
          <div className="mt-2 sm:mt-3 lg:mt-4">
            <p className="text-white text-sm sm:text-sm">
              Based on {commissionData?.annual_gold_kg?.toLocaleString() || '0'}KG projected annual gold output (${annualPerShare.toLocaleString(undefined, { maximumFractionDigits: 2 })}/share/year)
            </p>
            {/* Calculation breakdown */}
            <div className="mt-3 p-3 rounded-lg border text-left text-xs leading-relaxed" style={{ backgroundColor: 'rgba(245, 158, 11, 0.08)', borderColor: 'rgba(245, 158, 11, 0.2)' }}>
              <p className="font-semibold text-yellow-400 mb-1">📐 How this is calculated:</p>
              <p className="text-gray-300">Gold production: <span className="text-white">{(staticProj?.goldProductionKgPerYear || 0).toLocaleString()} kg{staticProj?.isRampYear ? ` in 2026 (${staticProj?.goldProductionKgPerMonth} kg/month × ${staticProj?.monthsOperating} operating months)` : '/year'}</span></p>
              <p className="text-gray-300">Gold price: <span className="text-white">${CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG.toLocaleString()}/kg</span></p>
              <p className="text-gray-300">Net margin (after {CALCULATION_CONSTANTS.REFINING_PERCENT}% refining + {CALCULATION_CONSTANTS.OPEX_PERCENT}% opex): <span className="text-white">50%</span></p>
              {dyn ? (
                <p className="text-gray-300">Shares used in calculation: <span className="text-green-400 font-semibold">{denominator.toLocaleString()} shares sold so far</span></p>
              ) : (
                <p className="text-gray-300">Shares used in calculation: <span className="text-white">Waiting for the live share count</span></p>
              )}
              <p className="text-gray-300">Annual dividend per share: <span className="text-green-400 font-semibold">${annualPerShare.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></p>
              <p className="text-gray-300 mt-1 pt-1 border-t border-white/10">
                Your shares: <span className="text-white">{userShares.toLocaleString(undefined, { maximumFractionDigits: 6 })}</span> × ${annualPerShare.toLocaleString(undefined, { maximumFractionDigits: 2 })} = <span className="text-yellow-400 font-bold">${liveProjected.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>/year
              </p>
            </div>
            <p className="text-gray-500 text-[10px] sm:text-xs mt-2 italic">
              Subject to actual gold production &amp; market conditions
            </p>
          </div>
        </div>
          );
        })()}

        {/* Whole Shares (Dividend Eligible) - Mobile Optimized */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-3 sm:p-4 lg:p-6 shadow-lg">
          <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <p className="text-white text-sm sm:text-sm font-medium">Whole Shares</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-400">
                {Math.floor(commissionData?.total_shares_owned || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-blue-600 p-2 sm:p-3 rounded-lg self-start sm:self-auto">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
          <div className="mt-2 sm:mt-3 lg:mt-4">
            <p className="text-white text-sm sm:text-sm">
              {DIVIDENDS_USER_VISIBLE ? 'Eligible for dividends' : 'Whole shares owned'}
            </p>
          </div>
        </div>

        {/* Fractional Shares - NEW */}
        <div className="bg-gray-800 rounded-xl border border-blue-900 p-3 sm:p-4 lg:p-6 shadow-lg">
          <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <p className="text-white text-sm sm:text-sm font-medium">Fractional Shares</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-300">
                {((commissionData?.total_shares_owned || 0) % 1).toFixed(6)}
              </p>
            </div>
            <div className="bg-blue-700 p-2 sm:p-3 rounded-lg self-start sm:self-auto">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
          <div className="mt-2 sm:mt-3 lg:mt-4">
            <p className="text-white text-sm sm:text-sm">
              From sponsor commissions
            </p>
          </div>
        </div>

        {/* Referral Count - Mobile Optimized */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-3 sm:p-4 lg:p-6 shadow-lg">
          <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <p className="text-white text-sm sm:text-sm font-medium">Referrals</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-400">
                {commissionData?.referral_count || 0}
              </p>
            </div>
            <div className="bg-purple-600 p-2 sm:p-3 rounded-lg self-start sm:self-auto">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-2 sm:mt-3 lg:mt-4">
            <p className="text-white text-sm sm:text-sm">
              People referred
            </p>
          </div>
        </div>
      </div>

      {/* Fractional Shares Educational Banner */}
      <div className="bg-gradient-to-r from-blue-900/50 to-green-900/50 rounded-xl border border-blue-700 p-4 sm:p-6 shadow-lg">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex-shrink-0">
            <div className="bg-blue-600 p-2 sm:p-3 rounded-lg">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-white mb-2">💡 Understanding Fractional Shares & USDT</h3>
            <div className="space-y-2 text-sm sm:text-base text-gray-300">
              <p>
                <strong className="text-blue-300">Fractional shares</strong> are earned from commissions when your <strong>direct sponsors/referrals</strong> make purchases or earn commissions themselves.
                These fractions accumulate over time and have real value!
              </p>
              <p>
                <strong className="text-yellow-300">Important:</strong> Fractional shares still have value and will eventually combine to form whole shares as you earn more commissions.
              </p>
              <p className="text-blue-200">
                📚 <button
                  onClick={() => setActiveSection('training')}
                  className="underline hover:text-blue-100 font-semibold"
                >
                  Learn more about how fractional shares work
                </button> in our training center.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Share Portfolio Breakdown - Mobile Optimized */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 lg:p-6 shadow-lg">
        <div className="mb-4">
          <div className="text-base lg:text-lg font-bold text-yellow-400 mb-2 flex items-center gap-2">
            <span className="text-xl">💎</span>
            TOTAL SHARE HOLDINGS: {Math.floor(commissionData?.total_shares_owned || 0).toLocaleString()} shares
          </div>
          <div className="text-sm lg:text-sm text-white ml-6">
            (All active)
          </div>

          {/* Dividend Eligibility Notice */}
          {DIVIDENDS_USER_VISIBLE && commissionData?.total_shares_owned && (commissionData.total_shares_owned - Math.floor(commissionData.total_shares_owned)) > 0 && (
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mt-3 ml-6">
              <div className="flex items-start space-x-2">
                <div className="text-blue-400 text-sm">ℹ️</div>
                <div>
                  <p className="text-blue-300 text-sm font-medium">Dividend Eligibility</p>
                  <p className="text-blue-200 text-sm">
                    Only <strong>whole shares</strong> qualify for dividend payments. You have <strong>{(commissionData.total_shares_owned - Math.floor(commissionData.total_shares_owned)).toFixed(6)} fractional shares</strong> earned from your direct sponsors' commissions.
                    These fractions still have value and will accumulate into whole shares over time!
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="text-sm lg:text-base font-bold text-white mb-3 flex items-center gap-2">
          <span className="text-lg">📈</span>
          SHARE BREAKDOWN:
        </div>

        <div className="space-y-3 lg:space-y-4 ml-4">
          {/* Direct Purchases */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-gray-300 text-sm lg:text-base">• Direct Purchases:</span>
              <span className="text-blue-400 font-semibold text-sm lg:text-base">
                {(commissionData?.direct_purchases || 0).toFixed(2)} shares
              </span>
            </div>
            <div className="text-white text-sm lg:text-sm ml-4">
              └ Cash/crypto share purchases
            </div>
          </div>

          {/* Bonus Shares - only show if > 0 */}
          {(commissionData?.bonus_shares || 0) > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-sm lg:text-base">• Tier Bonus Shares:</span>
                <span className="text-green-400 font-semibold text-sm lg:text-base">
                  +{(commissionData?.bonus_shares || 0).toFixed(2)} shares
                </span>
              </div>
              <div className="text-white text-sm lg:text-sm ml-4">
                └ Bonus from purchase tiers
              </div>
            </div>
          )}

          {/* Commission Converted to Shares */}
          {(commissionData?.converted_shares || 0) > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-sm lg:text-base">• Commission Converted:</span>
                <span className="text-purple-400 font-semibold text-sm lg:text-base">
                  {(commissionData?.converted_shares || 0).toFixed(2)} shares
                </span>
              </div>
              <div className="text-white text-sm lg:text-sm ml-4">
                └ ${commissionData?.total_usdt_converted?.toFixed(2) || '0.00'} USDT converted
              </div>
            </div>
          )}

          {/* Presale Commission Shares - only show if > 0 */}
          {(commissionData?.presale_commission_shares || 0) > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-sm lg:text-base">• Presale Commission Shares:</span>
                <span className="text-yellow-400 font-semibold text-sm lg:text-base">
                  {(commissionData?.presale_commission_shares || 0).toFixed(6)} shares
                </span>
              </div>
              <div className="text-white text-sm lg:text-sm ml-4">
                └ 15% presale referral bonus (ended 6 Nov 2025)
              </div>
            </div>
          )}

          {/* 5% Commission Share Purchases - displayed as PURCHASES */}
          {(commissionData?.commission_level_shares || 0) > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-sm lg:text-base">• 5% Commission Share Purchases:</span>
                <span className="text-orange-400 font-semibold text-sm lg:text-base">
                  {(commissionData?.commission_level_shares || 0).toFixed(6)} shares
                </span>
              </div>

              {/* Calculate and show total purchase value */}
              {(() => {
                const totalValue = commissionData?.commission_share_purchases?.reduce(
                  (sum: number, p: any) => sum + parseFloat(p.total_amount || 0), 0
                ) || 0;
                return (
                  <div className="text-green-400 text-sm lg:text-sm ml-4 font-semibold">
                    └ Total value: ${totalValue.toFixed(2)} purchased on your behalf
                  </div>
                );
              })()}

              {/* Show individual purchase records */}
              {commissionData?.commission_share_purchases?.length > 0 && (
                <div className="mt-3 ml-4 bg-gray-900/50 rounded-lg p-3 border border-orange-900/30">
                  <div className="text-sm text-orange-300 font-semibold mb-2 flex items-center gap-2">
                    <span>🛒</span> Recent 5% Commission Purchases Made For You:
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {commissionData.commission_share_purchases.slice(0, 10).map((purchase: any, index: number) => {
                      const amount = parseFloat(purchase.total_amount || 0);
                      const shares = parseFloat(purchase.shares_purchased || 0);
                      const date = new Date(purchase.purchase_date || purchase.created_at);
                      const level = purchase.payment_method?.replace('COMMISSION_LEVEL_', 'L') || '';
                      return (
                        <div key={purchase.id || index} className="flex justify-between items-center text-sm bg-gray-800/50 p-2 rounded border-l-2 border-orange-500">
                          <div className="flex flex-col">
                            <span className="text-gray-300">
                              {date.toLocaleDateString()} {level && <span className="text-orange-400">({level})</span>}
                            </span>
                            <span className="text-gray-500 text-[10px]">5% from referral purchase</span>
                          </div>
                          <div className="text-right">
                            <div className="text-orange-400 font-semibold">{shares.toFixed(6)} shares</div>
                            <div className="text-green-400 text-[10px]">${amount.toFixed(2)} value</div>
                          </div>
                        </div>
                      );
                    })}
                    {commissionData.commission_share_purchases.length > 10 && (
                      <div className="text-center text-gray-500 text-sm py-1">
                        + {commissionData.commission_share_purchases.length - 10} more purchases
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Legacy Shares - only show if > 0 */}
          {(commissionData?.legacy_shares || 0) > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-sm lg:text-base">• Legacy Bonus Shares:</span>
                <span className="text-indigo-400 font-semibold text-sm lg:text-base">
                  +{(commissionData?.legacy_shares || 0).toFixed(2)} shares
                </span>
              </div>
              <div className="text-white text-sm lg:text-sm ml-4">
                └ Historical bonus allocation
              </div>
            </div>
          )}

          {/* Share Transfers */}
          {((commissionData?.shares_received || 0) > 0 || (commissionData?.shares_sent || 0) > 0) && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-sm lg:text-base">• Net Transferred Shares:</span>
                <span className={`font-semibold text-sm lg:text-base ${
                  (commissionData?.net_transferred_shares || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {(commissionData?.net_transferred_shares || 0) >= 0 ? '+' : ''}{(commissionData?.net_transferred_shares || 0).toFixed(2)} shares
                </span>
              </div>
              <div className="text-white text-sm lg:text-sm ml-4 space-y-1">
                <div>└ Received: {(commissionData?.shares_received || 0).toFixed(2)} shares</div>
                <div>└ Sent: {(commissionData?.shares_sent || 0).toFixed(2)} shares</div>
              </div>
            </div>
          )}

          {/* TOTAL LINE - shows sum that should match header */}
          <div className="pt-2 mt-2 border-t border-gray-600">
            <div className="flex items-center justify-between">
              <span className="text-white font-bold text-sm lg:text-base">= TOTAL:</span>
              <span className="text-cyan-400 font-bold text-sm lg:text-base">
                {(commissionData?.total_shares_owned || 0).toFixed(2)} shares
              </span>
            </div>
            <div className="text-white text-sm lg:text-sm ml-4">
              └ {commissionData?.whole_shares || 0} whole + {(commissionData?.fractional_shares || 0).toFixed(6)} fractional
            </div>
          </div>
        </div>

        {/* Account Balance Section */}
        <div className="mt-4 lg:mt-6 pt-4 border-t border-gray-600">
          <div className="text-sm lg:text-base font-bold text-white mb-2 flex items-center gap-2">
            <span className="text-lg">💰</span>
            COMMISSION STATUS:
          </div>
          <div className="space-y-2 ml-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-300 text-sm lg:text-base">• Available Balance:</span>
              <span className="text-green-400 font-semibold text-sm lg:text-base">
                ${clientWithdrawableBalance.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-300 text-sm lg:text-base">• Total USDT Earned:</span>
              <span className="text-blue-400 font-semibold text-sm lg:text-base">
                ${commissionData?.total_earned_usdt?.toFixed(2) || '0.00'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Referral Link Section - New Requirements-Based Component */}
      <ReferralLinkRequirements
        userId={validatedUserId || 0}
        referralLink={referralLink || `https://aureus.africa/${user?.database_user?.username || user?.username || ''}`}
        onNavigateToKYC={() => {
          window.location.href = '/dashboard?section=kyc';
        }}
        onNavigateToPurchase={() => {
          window.location.href = '/dashboard?section=purchase-shares';
        }}
      />
      {/* Birthday 2026 package cards (auto-hidden outside the promo window) */}
      <div className="w-full max-w-full">
        <BirthdayPackageButtons />
      </div>
      {/* Quick Actions - Mobile Optimized */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 lg:p-6 shadow-lg w-full max-w-full overflow-x-hidden box-border">
        <h3 className="text-base lg:text-lg font-semibold text-white mb-3 lg:mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 w-full max-w-full">
          <button
            onClick={() => setShowCreateUserModal(true)}
            className="bg-yellow-600 hover:bg-yellow-700 active:bg-yellow-800 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 text-sm lg:text-base touch-manipulation w-full flex items-center justify-center gap-2"
          >
            <span>➕</span>
            <span>Create User Account</span>
          </button>

          <button
            onClick={() => setActiveSection('referrals')}
            className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 text-sm lg:text-base touch-manipulation w-full"
          >
            Manage Referrals
          </button>

          <button
            onClick={() => setActiveSection('commissions')}
            className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 text-sm lg:text-base touch-manipulation w-full"
          >
            Withdraw USDT
          </button>

          <button
            onClick={() => setActiveSection('marketing-tools')}
            className="bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 text-sm lg:text-base touch-manipulation w-full"
          >
            Marketing Tools
          </button>
        </div>
      </div>

      {/* Pro Tip - Mobile Optimized */}
      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4 lg:p-6 shadow-lg w-full max-w-full overflow-x-hidden box-border">
        <div className="flex items-start space-x-3">
          <div className="text-yellow-500 text-xl lg:text-2xl flex-shrink-0">💡</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-yellow-400 font-semibold mb-2 text-sm lg:text-base">Pro Tip</h3>
            <p className="text-yellow-300 text-sm lg:text-base break-words">
              Want to buy shares too? Switch to the Shareholder Dashboard using the switcher above to purchase your own shares and build your portfolio!
            </p>
          </div>
        </div>
      </div>

      {/* How To Start Training Videos */}
      <HowToStartVideos
        pageType="affiliate-dashboard"
        title="Training Videos"
      />
    </div>
  );

  const renderSectionContent = () => {
    // CRITICAL: Check if we have a valid user ID before rendering sections that need it
    const sectionsRequiringUserId: AffiliateSection[] = ['referrals', 'commissions', 'marketing-tools', 'messages', 'training'];

    if (sectionsRequiringUserId.includes(activeSection) && !validatedUserId) {
      return (
        <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-6 text-center">
          <h3 className="text-yellow-400 text-lg font-semibold mb-2">⚠️ Session Issue Detected</h3>
          <p className="text-gray-300 mb-4">
            We couldn't verify your user session. This may happen if your session has expired.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-yellow-600 hover:bg-yellow-500 text-white px-4 py-2 rounded mr-2"
          >
            🔄 Refresh Page
          </button>
          <button
            onClick={() => {
              localStorage.clear();
              window.location.href = '/login';
            }}
            className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded"
          >
            🔑 Re-login
          </button>
        </div>
      );
    }

    switch (activeSection) {
      case 'overview':
        return renderOverview();
      case 'referrals':
        return (
          <ReferralCenter
            userId={validatedUserId || 0}
            className="bg-gray-800 rounded-lg border border-gray-700 p-3 sm:p-6"
          />
        );

      case 'commissions':
        return (
          <ComprehensiveAffiliateManager
            userId={validatedUserId || 0}
            onDataRefresh={loadCommissionData}
            onNavigateToSupport={() => {
              // Switch to shareholder dashboard and navigate to support center
              if (onSwitchDashboard) {
                window.history.pushState({}, '', '/dashboard?section=support-center');
                onSwitchDashboard('shareholder');
              }
            }}
          />
        );
      case 'sales-analytics':
        return (
          <SalesAnalytics className="w-full" />
        );
      case 'marketing-tools':
        return (
          <MarketingToolsManager
            userId={validatedUserId || 0}
            affiliateData={{
              user: user?.database_user,
              referralCount: commissionData?.referral_count || 0,
              commissionBalance: commissionData
            }}
          />
        );
      case 'updates':
        return <UpdatesPage audience="affiliate" />;
      case 'messages':
        return (
          <MessageDashboard
            userId={validatedUserId || 0}
            className="bg-gray-800 rounded-lg border border-gray-700 p-6"
          />
        );
      case 'training':
        return (
          <AffiliateTrainingCenter
            userId={validatedUserId || 0}
            className="bg-gray-800 rounded-lg border border-gray-700 p-6"
          />
        );
      case 'gold-diggers':
        return (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">🏆 Gold Diggers Competition</h2>
            <p className="text-white mb-6">
              Compete for massive USDT prizes! Track your ranking and see the leaderboard.
            </p>
            <GoldDiggersClub howToEnter />
          </div>
        );
      case 'gold-diggers-pool':
        return (
          <GoldDiggersDashboard
            user={user}
            onBack={() => setActiveSection('overview')}
          />
        );
      case 'vein-accord':
        return isGlobalTopAffiliatePoolTester(user?.database_user?.username || user?.username)
          ? <ProductionVeinAccord />
          : <p className="text-gray-300">The Global Top Affiliate Pool is not open for this account.</p>;
      case 'zimbabwe-awaits':
        return (
          <ZimbabweAwaitsPage
            onPurchaseShares={() => {
              window.history.pushState({}, '', '/dashboard?section=purchase-shares');
              onSwitchDashboard('shareholder');
            }}
            onRallyTeam={() => {
              window.history.pushState({}, '', '/dashboard?section=referrals');
              setActiveSection('referrals');
            }}
            currentUsername={user?.database_user?.username || user?.username}
          />
        );
      case 'founding-shareholders':
        return (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <FoundingShareholdersShowcase />
          </div>
        );
      case 'settings':
        return (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">⚙️ Settings</h2>
            <p className="text-white mb-6">
              Manage your account preferences and security settings.
            </p>
            {/* Settings Messages */}
            {settingsMessage && (
              <div className={`rounded-lg p-4 mb-4 ${
                settingsMessage.type === 'success'
                  ? 'bg-green-900/20 border border-green-500/30 text-green-300'
                  : 'bg-red-900/20 border border-red-500/30 text-red-300'
              }`}>
                <p className="text-sm">
                  {settingsMessage.type === 'success' ? '✅' : '❌'} {settingsMessage.text}
                </p>
              </div>
            )}

            <div className="space-y-4">
              {/* Profile Picture Section */}
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-2">📷 Profile Picture</h3>
                <p className="text-gray-300 text-sm mb-4">Upload a professional profile picture</p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <ProfilePictureUpload
                    currentImageUrl={user?.database_user?.profile_image_url}
                    userId={validatedUserId || 0}
                    onImageUpdate={handleProfilePictureUpdate}
                    size="large"
                    editable={true}
                    showSaveButton={true}
                    autoSave={false}
                    userInitials={(
                      user?.database_user?.full_name ||
                      user?.user_metadata?.full_name ||
                      user?.database_user?.first_name ||
                      user?.username ||
                      user?.email?.split('@')[0] ||
                      'User'
                    ).charAt(0).toUpperCase()}
                    userName={
                      user?.database_user?.full_name ||
                      user?.user_metadata?.full_name ||
                      user?.database_user?.first_name ||
                      user?.username ||
                      user?.email?.split('@')[0] ||
                      'User'
                    }
                  />
                  <div>
                    <p className="text-white font-medium mb-2">Upload Guidelines:</p>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li>• Use a clear, professional photo</li>
                      <li>• Maximum file size: 5MB</li>
                      <li>• Supported formats: JPG, PNG, GIF</li>
                      <li>• Square images work best</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-2">👤 Profile Settings</h3>
                <p className="text-gray-300 text-sm mb-4">
                  Update your personal information
                  <span className="block md:hidden text-sm text-yellow-400 mt-1">💡 Tap any field to edit</span>
                </p>
                {mustCompleteProfile ? (
                  <p className="text-sm text-amber-200 bg-amber-900/30 border border-amber-600/40 rounded-lg px-3 py-2 mb-4">
                    {PROFILE_DETAILS_MISSING_NOTE}
                  </p>
                ) : null}

                {/* Display Current Profile Information */}
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                      <div
                        className="bg-gray-600 rounded-lg px-3 py-2 text-white"
                        style={{ minHeight: '44px', display: 'flex', alignItems: 'center' }}
                      >
                        {splitStoredName(user?.database_user).name || 'Not set'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Surname</label>
                      <div
                        className="bg-gray-600 rounded-lg px-3 py-2 text-white"
                        style={{ minHeight: '44px', display: 'flex', alignItems: 'center' }}
                      >
                        {splitStoredName(user?.database_user).surname || 'Not set'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                      <div
                        onClick={openProfileEditor}
                        className="bg-gray-600 rounded-lg px-3 py-2 text-white cursor-pointer hover:bg-gray-500 transition-colors md:cursor-default md:hover:bg-gray-600 lg:cursor-default lg:hover:bg-gray-600"
                        style={{ minHeight: '44px', display: 'flex', alignItems: 'center' }}
                      >
                        {user?.database_user?.email || user?.email || 'Not set'}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Phone Number</label>
                      <div
                        onClick={openProfileEditor}
                        className="bg-gray-600 rounded-lg px-3 py-2 text-white cursor-pointer hover:bg-gray-500 transition-colors md:cursor-default md:hover:bg-gray-600 lg:cursor-default lg:hover:bg-gray-600"
                        style={{ minHeight: '44px', display: 'flex', alignItems: 'center' }}
                      >
                        {user?.database_user?.phone || 'Not set'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Country</label>
                      <div
                        onClick={openProfileEditor}
                        className="bg-gray-600 rounded-lg px-3 py-2 text-white cursor-pointer hover:bg-gray-500 transition-colors md:cursor-default md:hover:bg-gray-600 lg:cursor-default lg:hover:bg-gray-600"
                        style={{ minHeight: '44px', display: 'flex', alignItems: 'center' }}
                      >
                        {(() => {
                          const countryCode = user?.database_user?.country_of_residence;
                          return countryCode ? getCountryName(countryCode) : 'Not set';
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Username</label>
                      <div className="bg-gray-600 rounded-lg px-3 py-2 text-white">
                        {user?.database_user?.username || 'Not set'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Account Type</label>
                      <div className="bg-gray-600 rounded-lg px-3 py-2 text-white">
                        Email Account
                      </div>
                    </div>
                  </div>

                  {/* Profile Description Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Profile Description</label>
                    <div
                      onClick={openProfileEditor}
                      className="bg-gray-600 rounded-lg px-3 py-2 text-white min-h-[60px] prose prose-invert max-w-none cursor-pointer hover:bg-gray-500 transition-colors md:cursor-default md:hover:bg-gray-600 lg:cursor-default lg:hover:bg-gray-600"
                      style={{ fontSize: '14px', lineHeight: '1.6' }}
                    >
                      {user?.database_user?.profile_description ? (
                        <div dangerouslySetInnerHTML={createSafeHtml(user.database_user.profile_description)} />
                      ) : (
                        'No description set'
                      )}
                    </div>
                  </div>

                  <div className="pt-2 space-y-3">
                    {selfServiceProfileEditOpen(user?.database_user) ? (
                      <button
                        onClick={openProfileEditor}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                      >
                        {missingProfileDetailFields(user?.database_user).length > 0 ? 'Update missing details' : 'Edit Profile'}
                      </button>
                    ) : null}
                    <p className="text-sm text-amber-200 bg-amber-900/30 border border-amber-600/40 rounded-lg px-3 py-2">
                      {missingProfileDetailFields(user?.database_user).length > 0
                        ? PROFILE_DETAILS_MISSING_NOTE
                        : selfServiceProfileEditOpen(user?.database_user)
                        ? `You can edit these details once after you register. After you save, this button is removed. The username cannot be changed here. ${PROFILE_DETAILS_ADMIN_NOTE}`
                        : `These details, including the username, can no longer be edited here. ${PROFILE_DETAILS_ADMIN_NOTE}`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-2">🔒 Security</h3>
                <p className="text-gray-300 text-sm mb-4">Password and security preferences</p>

                <div className="space-y-4">
                  {/* Password Change */}
                  <div className="border-b border-gray-600 pb-4">
                    <h4 className="text-white font-medium mb-2">Change Password</h4>
                    <p className="text-white text-sm mb-3">Update your account password</p>
                    <button
                      onClick={() => setShowPasswordChangeModal(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      Change Password
                    </button>
                  </div>

                  {/* 2FA Email Verification */}
                  <div className="border-b border-gray-600 pb-4">
                    <h4 className="text-white font-medium mb-2">Two-Factor Authentication (2FA)</h4>
                    <p className="text-white text-sm mb-3">
                      Email verification for withdrawals and sensitive operations
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">Email Verification</p>
                        <p className="text-white text-sm">
                          {user?.database_user?.two_factor_enabled ?
                            'Enabled - Email verification for withdrawals' :
                            'Disabled - Enable for enhanced security'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={user?.database_user?.two_factor_enabled || false}
                          onChange={(e) => handle2FAToggle(e.target.checked)}
                          disabled={settingsLoading}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                      </label>
                    </div>
                  </div>

                  {/* REMOVED: Account Security Status section - Email verification is unnecessary as emails are verified during registration */}
                </div>
              </div>

              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-2">🔔 Notifications</h3>
                <p className="text-gray-300 text-sm mb-4">Notification preferences</p>

                <div className="space-y-4">
                  {/* Email Notifications */}
                  <div className="border-b border-gray-600 pb-4">
                    <h4 className="text-white font-medium mb-3">Email Notifications</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">Payment Confirmations</p>
                          <p className="text-white text-sm">Share purchases and commission payments</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.payment_confirmations}
                            onChange={(e) => handleNotificationPreferenceChange('payment_confirmations', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">Security Alerts</p>
                          <p className="text-white text-sm">Login attempts and security changes</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.security_alerts}
                            onChange={(e) => handleNotificationPreferenceChange('security_alerts', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">Commission Updates</p>
                          <p className="text-white text-sm">New referrals and commission earnings</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.commission_updates}
                            onChange={(e) => handleNotificationPreferenceChange('commission_updates', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">Company Updates</p>
                          <p className="text-white text-sm">Mining operations and company news</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.marketing_emails}
                            onChange={(e) => handleNotificationPreferenceChange('marketing_emails', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Dashboard Notifications */}
                  <div className="border-b border-gray-600 pb-4">
                    <h4 className="text-white font-medium mb-3">Dashboard Notifications</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">In-App Notifications</p>
                          <p className="text-white text-sm">Show notifications in dashboard</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.in_app_notifications}
                            onChange={(e) => handleNotificationPreferenceChange('in_app_notifications', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">Sound Notifications</p>
                          <p className="text-white text-sm">Play sound for important alerts</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.sound_notifications}
                            onChange={(e) => handleNotificationPreferenceChange('sound_notifications', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Notification Frequency */}
                  <div>
                    <h4 className="text-white font-medium mb-3">Notification Frequency</h4>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notification_frequency"
                          value="immediate"
                          checked={notificationPreferences.notification_frequency === 'immediate'}
                          onChange={(e) => handleNotificationPreferenceChange('notification_frequency', e.target.value)}
                          className="w-4 h-4 text-yellow-600 bg-gray-600 border-gray-500 focus:ring-yellow-500"
                        />
                        <span className="ml-2 text-white text-sm">Immediate</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notification_frequency"
                          value="daily"
                          checked={notificationPreferences.notification_frequency === 'daily'}
                          onChange={(e) => handleNotificationPreferenceChange('notification_frequency', e.target.value)}
                          className="w-4 h-4 text-yellow-600 bg-gray-600 border-gray-500 focus:ring-yellow-500"
                        />
                        <span className="ml-2 text-white text-sm">Daily Summary</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notification_frequency"
                          value="weekly"
                          checked={notificationPreferences.notification_frequency === 'weekly'}
                          onChange={(e) => handleNotificationPreferenceChange('notification_frequency', e.target.value)}
                          className="w-4 h-4 text-yellow-600 bg-gray-600 border-gray-500 focus:ring-yellow-500"
                        />
                        <span className="ml-2 text-white text-sm">Weekly Summary</span>
                      </label>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      onClick={handleSaveNotificationPreferences}
                      disabled={notificationPreferencesLoading}
                      className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      {notificationPreferencesLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      )}
                      <span>
                        {notificationPreferencesLoading ? 'Saving...' : 'Save Notification Preferences'}
                      </span>
                    </button>

                    {/* Success/Error Message */}
                    {notificationPreferencesMessage && (
                      <div className={`mt-3 p-3 rounded-lg ${
                        notificationPreferencesMessage.type === 'success'
                          ? 'bg-green-600 text-white'
                          : 'bg-red-600 text-white'
                      }`}>
                        {notificationPreferencesMessage.text}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
      </div>
    );
  }

  return (
    <MasterAdminViewWrapper>
      <div className="min-h-screen bg-gray-900 flex">
        {/* Desktop Sidebar - Hidden on mobile */}
        <div className="hidden lg:flex lg:fixed lg:w-64 lg:flex-col" style={{ top: '40px', bottom: 0 }}>
          <div className="flex flex-col h-full bg-gray-800 border-r border-gray-700">
            {/* Header */}
            <div className="p-4 border-b border-gray-700">
              <DashboardSwitcher
                currentDashboard="affiliate"
                onSwitch={onSwitchDashboard}
                user={user}
              />
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
              {affiliateNavigation.filter((item) => item.id !== 'vein-accord' || isGlobalTopAffiliatePoolTester(user?.database_user?.username || user?.username)).map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    if (mustCompleteProfile && item.id !== 'settings') return;
                    setActiveSection(item.id);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    activeSection === item.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <span className="text-xl">{item.icon}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.label}</span>
                      {(item as any).isNew && !localStorage.getItem(`aureus_visited_${item.id}`) && (
                        <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase">New</span>
                      )}
                    </div>
                    <div className="text-sm opacity-75">{item.description}</div>
                  </div>
                </button>
              ))}
            </nav>

            {/* User Info */}
            <div className="p-4 border-t border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {(user?.database_user?.full_name || user?.email)?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">
                    {user?.database_user?.full_name || user?.email?.split('@')[0]}
                  </p>
                  <p className="text-white text-sm">Affiliate</p>
                </div>
              </div>
              <button
                onClick={onLogout}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white py-2 px-3 rounded-lg text-sm transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu - Only shown when open */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop overlay */}
            <div
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Mobile Menu */}
            <div className="lg:hidden fixed inset-y-0 left-0 w-80 bg-gray-800 z-50 shadow-xl border-r border-gray-700">
              <div className="flex flex-col h-full">
                {/* Mobile Menu Header with Logo, Title and Close Button */}
                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo-s.png"
                      alt="Aureus Alliance Holdings"
                      className="w-10 h-10"
                    />
                    <span className="text-xl font-bold text-white">Aureus Alliance</span>
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Dashboard Switcher */}
                {onSwitchDashboard && (
                  <div className="p-4 border-b border-gray-700">
                    <DashboardSwitcher
                      currentDashboard="affiliate"
                      onSwitch={onSwitchDashboard}
                      user={user}
                    />
                  </div>
                )}

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                  {affiliateNavigation.filter((item) => item.id !== 'vein-accord' || isGlobalTopAffiliatePoolTester(user?.database_user?.username || user?.username)).map((item) => {
                    const isActive = activeSection === item.id;

                    return (
                      <div
                        key={item.id}
                        onClick={() => {
                          if (mustCompleteProfile && item.id !== 'settings') return;
                          setActiveSection(item.id);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`
                          group relative p-3 rounded-lg cursor-pointer transition-all duration-200
                          ${isActive
                            ? 'bg-blue-600/20 border border-blue-500/30 text-blue-400'
                            : 'hover:bg-gray-700/50 text-gray-300 hover:text-white'
                          }
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`
                            p-2 rounded-lg transition-colors
                            ${isActive ? 'bg-blue-500/20' : 'bg-gray-700/50 group-hover:bg-gray-600/50'}
                          `}>
                            <span className="text-xl">{item.icon}</span>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm truncate">{item.label}</span>
                              {(item as any).isNew && !localStorage.getItem(`aureus_visited_${item.id}`) && (
                                <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase">New</span>
                              )}
                            </div>
                            <p className="text-sm text-white mt-1 truncate">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </nav>

                {/* Mobile User Info */}
                <div className="border-t border-gray-700">
                  {/* Referral Link - Mobile Version with New Requirements */}
                  <div className="p-3 bg-gray-900/30 border-b border-gray-700">
                    <ReferralLinkRequirements
                      userId={validatedUserId || 0}
                      referralLink={referralLink}
                      onNavigateToKYC={() => {
                        setIsMobileMenuOpen(false);
                        window.location.href = '/dashboard?section=kyc';
                      }}
                      onNavigateToPurchase={() => {
                        setIsMobileMenuOpen(false);
                        window.location.href = '/dashboard?section=purchase-shares';
                      }}
                    />
                  </div>

                  {/* User Profile */}
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <ProfilePictureUpload
                          currentImageUrl={user?.database_user?.profile_image_url}
                          userId={validatedUserId || 0}
                          onImageUpdate={handleProfilePictureUpdate}
                          size="small"
                          editable={false}
                          userInitials={(user?.database_user?.full_name || user?.username || user?.email)?.charAt(0).toUpperCase() || 'A'}
                          userName={user?.database_user?.full_name || user?.username || user?.email?.split('@')[0] || 'Affiliate'}
                        />
                        <div>
                          <p className="text-white text-sm font-medium">
                            {user?.database_user?.full_name || user?.username || user?.email?.split('@')[0]}
                          </p>
                          <p className="text-white text-sm">Affiliate</p>
                        </div>
                      </div>
                      <button
                        onClick={onLogout}
                        className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
                        title="Logout"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Main Content */}
        <div className="flex-1 lg:ml-64">
          {/* Mobile Header - Hidden when menu is open */}
          {!isMobileMenuOpen && (
            <div className="lg:hidden bg-gray-800 border-b border-gray-700 sticky z-50" style={{ display: 'block !important', top: '40px' }}>
              <div className="flex items-center justify-between p-4" style={{ width: '100%' }}>
                <div className="flex items-center gap-3" style={{ flex: '1' }}>
                  <img
                    src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo-s.png"
                    alt="Aureus Alliance Holdings"
                    className="w-8 h-8"
                  />
                  <span className="font-bold text-lg text-white">Aureus Alliance</span>
                </div>

                {/* Mobile Menu Button - ALWAYS VISIBLE */}
                <button
                  onClick={() => {
                    console.log('Menu button clicked!');
                    setIsMobileMenuOpen(!isMobileMenuOpen);
                  }}
                  className="p-2 rounded-lg bg-yellow-600 hover:bg-yellow-700 transition-colors flex-shrink-0"
                  aria-label="Toggle menu"
                  style={{
                    minWidth: '40px',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    visibility: 'visible',
                    opacity: 1,
                    position: 'relative',
                    zIndex: 9999
                  } as React.CSSProperties}
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ display: 'block', pointerEvents: 'none' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Content - Add padding-top for news ticker */}
          <div className="p-3 sm:p-6 w-full max-w-full overflow-x-hidden box-border" style={{ paddingTop: '52px' }}>
            {renderSectionContent()}
          </div>

          {/* Version Footer */}
          <div className="border-t border-gray-700 p-4 text-center">
            <p className="text-gray-500 text-sm">
              Aureus Africa {getFullVersion()} • {new Date().getFullYear()} Aureus Alliance Holdings (Pty) Ltd
            </p>
          </div>
        </div>

        {/* Password Change Modal */}
        {showPasswordChangeModal && user?.database_user && extractNumericId(user?.database_user?.id) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="max-w-md w-full">
              <SecurePasswordChangeForm
                userId={extractNumericId(user.database_user.id)!}
                userEmail={user.database_user.email || user.email}
                currentUsername={user.database_user.username || ''}
                onPasswordChanged={() => {
                  setSettingsMessage({
                    type: 'success',
                    text: 'Password changed successfully!'
                  });
                  setTimeout(() => setSettingsMessage(null), 3000);
                }}
                onClose={() => setShowPasswordChangeModal(false)}
              />
            </div>
          </div>
        )}

        {/* Profile Editor Modal */}
        {showProfileEditor && user?.database_user && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="max-w-md w-full bg-gray-800 rounded-lg border border-gray-700 p-6">
              <h2 className="text-xl font-bold text-white mb-4">Edit Profile</h2>
              {settingsMessage?.type === 'error' && (
                <div className="mb-4 rounded-lg border border-red-500/40 bg-red-900/30 px-3 py-2 text-sm text-red-200">
                  {settingsMessage.text}
                </div>
              )}

              <div className="space-y-4">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Name and surname * <span className="text-sm text-white">(Both are required)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      value={profileEditorData.full_name}
                      onChange={(e) => setProfileEditorData(prev => ({ ...prev, full_name: e.target.value }))}
                      className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 ${
                        profileValidation.full_name.status === 'valid' ? 'border-green-500 focus:ring-green-500' :
                        profileValidation.full_name.status === 'invalid' ? 'border-red-500 focus:ring-red-500' :
                        'border-gray-600 focus:ring-yellow-500'
                      }`}
                      placeholder="Enter your first and last name"
                    />
                    {profileValidation.full_name.status === 'checking' && (
                      <span className="absolute right-3 top-2.5 text-yellow-400">🔍 Checking...</span>
                    )}
                    {profileValidation.full_name.status === 'valid' && (
                      <span className="absolute right-3 top-2.5 text-green-400">✅</span>
                    )}
                    {profileValidation.full_name.status === 'invalid' && (
                      <span className="absolute right-3 top-2.5 text-red-400">❌</span>
                    )}
                  </div>
                  {profileValidation.full_name.message && (
                    <p className={`mt-1 text-sm ${
                      profileValidation.full_name.status === 'valid' ? 'text-green-400' :
                      profileValidation.full_name.status === 'invalid' ? 'text-red-400' :
                      'text-yellow-400'
                    }`}>
                      {profileValidation.full_name.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                  <div className="relative">
                    <input
                      type="email"
                      required
                      value={profileEditorData.email}
                      onChange={(e) => setProfileEditorData(prev => ({ ...prev, email: e.target.value }))}
                      className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 ${
                        profileValidation.email.status === 'valid' ? 'border-green-500 focus:ring-green-500' :
                        profileValidation.email.status === 'invalid' ? 'border-red-500 focus:ring-red-500' :
                        'border-gray-600 focus:ring-yellow-500'
                      }`}
                      placeholder="Enter your email address"
                    />
                    {profileValidation.email.status === 'checking' && (
                      <span className="absolute right-3 top-2.5 text-yellow-400">🔍 Checking...</span>
                    )}
                    {profileValidation.email.status === 'valid' && (
                      <span className="absolute right-3 top-2.5 text-green-400">✅</span>
                    )}
                    {profileValidation.email.status === 'invalid' && (
                      <span className="absolute right-3 top-2.5 text-red-400">❌</span>
                    )}
                  </div>
                  {profileValidation.email.message && (
                    <p className={`mt-1 text-sm ${
                      profileValidation.email.status === 'valid' ? 'text-green-400' :
                      profileValidation.email.status === 'invalid' ? 'text-red-400' :
                      'text-yellow-400'
                    }`}>
                      {profileValidation.email.message}
                    </p>
                  )}
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Country</label>
                  <div className="relative">
                    <select
                      value={profileEditorData.country_of_residence}
                      onChange={(e) => setProfileEditorData(prev => ({ ...prev, country_of_residence: e.target.value }))}
                      className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 appearance-none ${
                        profileValidation.country_of_residence.status === 'valid' ? 'border-green-500 focus:ring-green-500' :
                        profileValidation.country_of_residence.status === 'invalid' ? 'border-red-500 focus:ring-red-500' :
                        'border-gray-600 focus:ring-yellow-500'
                      }`}
                    >
                      <option value="">Select a country</option>
                      {countryOptions.map(country => (
                        <option key={country.value} value={country.value}>
                          {country.flag} {country.label}
                        </option>
                      ))}
                    </select>
                    {profileValidation.country_of_residence.status === 'valid' && (
                      <span className="absolute right-3 top-2.5 text-green-400 pointer-events-none">✅</span>
                    )}
                    {profileValidation.country_of_residence.status === 'invalid' && (
                      <span className="absolute right-3 top-2.5 text-red-400 pointer-events-none">❌</span>
                    )}
                  </div>
                  {profileValidation.country_of_residence.message && (
                    <p className={`mt-1 text-sm ${
                      profileValidation.country_of_residence.status === 'valid' ? 'text-green-400' :
                      profileValidation.country_of_residence.status === 'invalid' ? 'text-red-400' :
                      'text-yellow-400'
                    }`}>
                      {profileValidation.country_of_residence.message}
                    </p>
                  )}
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number {profileEditorData.country_of_residence && <span className="text-sm text-white">({getPhoneCodeForCountry(profileEditorData.country_of_residence)})</span>}
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={profileEditorData.phone}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (profileEditorData.country_of_residence && value && !value.startsWith('+')) {
                          const formatted = formatPhoneWithCountryCode(value, profileEditorData.country_of_residence);
                          setProfileEditorData(prev => ({ ...prev, phone: formatted }));
                        } else {
                          setProfileEditorData(prev => ({ ...prev, phone: value }));
                        }
                      }}
                      className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 ${
                        profileValidation.phone.status === 'valid' ? 'border-green-500 focus:ring-green-500' :
                        profileValidation.phone.status === 'invalid' ? 'border-red-500 focus:ring-red-500' :
                        'border-gray-600 focus:ring-yellow-500'
                      }`}
                      placeholder={profileEditorData.country_of_residence ? `${getPhoneCodeForCountry(profileEditorData.country_of_residence)} 12 345 6789` : 'Enter your phone number'}
                    />
                    {profileValidation.phone.status === 'checking' && (
                      <span className="absolute right-3 top-2.5 text-yellow-400">🔍 Checking...</span>
                    )}
                    {profileValidation.phone.status === 'valid' && (
                      <span className="absolute right-3 top-2.5 text-green-400">✅</span>
                    )}
                    {profileValidation.phone.status === 'invalid' && (
                      <span className="absolute right-3 top-2.5 text-red-400">❌</span>
                    )}
                  </div>
                  {profileValidation.phone.message && (
                    <p className={`mt-1 text-sm ${
                      profileValidation.phone.status === 'valid' ? 'text-green-400' :
                      profileValidation.phone.status === 'invalid' ? 'text-red-400' :
                      'text-yellow-400'
                    }`}>
                      {profileValidation.phone.message}
                    </p>
                  )}
                </div>

                {/* Profile Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Profile Description (WYSIWYG Editor)</label>
                  <ProfileDescriptionEditor
                    value={profileEditorData.profile_description}
                    onChange={(value) => setProfileEditorData(prev => ({ ...prev, profile_description: value }))}
                  />
                  <p className="text-sm text-white mt-2">Format your description with bold, italic, lists, and more. This will be displayed on your affiliate landing page.</p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowProfileEditor(false)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  disabled={settingsLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleProfileUpdate}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
                  disabled={settingsLoading}
                >
                  {settingsLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create User Modal */}
        <CreateUserModal
          isOpen={showCreateUserModal}
          onClose={() => setShowCreateUserModal(false)}
          onUserCreated={() => {
            // Refresh commission data to show updated referral count
            loadCommissionData();
          }}
          affiliateUser={user?.database_user || user}
        />

        {/* 2FA Email-PIN Verification Modal */}
        {show2FAPinModal && user?.database_user?.email && (extractNumericId(user?.database_user?.id) as any) > 0 && (
          <PinVerificationModal
            isOpen={show2FAPinModal}
            onClose={handle2FAPinModalClose}
            onVerified={handle2FAVerified as any}
            userId={extractNumericId(user?.database_user?.id) as any}
            email={user.database_user.email}
            purpose="security_change"
            title="2FA Security Verification"
            description={`Enter the 6-digit code sent to your email to ${pending2FAState ? 'enable' : 'disable'} Two-Factor Authentication.`}
          />
        )}

        {/* Live Shareholder Activity Feed - Hidden on mobile devices */}
        <div className="hidden lg:block">
          <LiveActivityFeed />
        </div>
      </div>
    </MasterAdminViewWrapper>
  );
};
